import { useRef, useState, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Sphere, Html } from '@react-three/drei';
import { Text } from '@qwickapps/react-framework';
import * as THREE from 'three';

// ── Types ─────────────────────────────────────────────────────────────────────

interface SystemNode {
  id: string;
  name: string;
  type: 'agent' | 'service' | 'infrastructure';
  status: 'online' | 'offline' | 'busy' | 'warning' | 'error';
  x: number;
  y: number;
  z: number;
  description?: string;
  metrics?: { label: string; value: string }[];
  controlUrl?: string;
  currentTask?: string;
  githubIssue?: string;
  lastDeploy?: string;
  blockerReason?: string;
}

/** Simplified agent shape accepted via the `agents` prop */
interface SimpleAgent {
  id: string;
  name: string;
  status: 'online' | 'offline' | 'busy' | 'warning' | 'error';
  x: number;
  y: number;
  z: number;
  type?: 'agent' | 'service' | 'infrastructure';
}

interface Connection {
  from: string;
  to: string;
  status: 'active' | 'inactive';
}

interface SystemGlobeProps {
  agents?: SimpleAgent[];
  nodes?: SystemNode[];
  connections?: Connection[];
  loading?: boolean;
  onNodeClick?: (node: SystemNode) => void;
}

// ── 3-D scene sub-components ──────────────────────────────────────────────────

function NetworkGlobe({
  nodes,
  connections,
  onNodeClick,
  selectedNodeId,
}: {
  nodes: SystemNode[];
  connections: Connection[];
  onNodeClick?: (node: SystemNode) => void;
  selectedNodeId?: string | null;
}) {
  const groupRef = useRef<THREE.Group>(null);

  useFrame(() => {
    if (groupRef.current) {
      groupRef.current.rotation.y += 0.001;
    }
  });

  const nodePositions = useMemo(() => {
    const pos: Record<string, [number, number, number]> = {};
    nodes.forEach(n => {
      pos[n.id] = [n.x, n.y, n.z];
    });
    return pos;
  }, [nodes]);

  return (
    <group ref={groupRef}>
      {/* Connections — native R3F line segments (no drei Line dependency) */}
      {connections.map((conn, i) => {
        const from = nodePositions[conn.from];
        const to = nodePositions[conn.to];
        if (!from || !to) return null;
        const pts = new Float32Array([...from, ...to]);
        return (
          <line key={i}>
            <bufferGeometry>
              <bufferAttribute
                attach="attributes-position"
                count={2}
                array={pts}
                itemSize={3}
              />
            </bufferGeometry>
            <lineBasicMaterial
              color={conn.status === 'active' ? '#4caf50' : '#666'}
              transparent
              opacity={conn.status === 'active' ? 0.8 : 0.3}
            />
          </line>
        );
      })}

      {nodes.map(node => (
        <NetworkNode
          key={node.id}
          node={node}
          onClick={onNodeClick}
          isSelected={selectedNodeId === node.id}
        />
      ))}
    </group>
  );
}

function NetworkNode({
  node,
  onClick,
  isSelected,
}: {
  node: SystemNode;
  onClick?: (node: SystemNode) => void;
  isSelected?: boolean;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);

  const statusColors: Record<string, string> = {
    online: '#4caf50',
    offline: '#9e9e9e',
    busy: '#ff9800',
    warning: '#ff9800',
    error: '#f44336',
  };

  useFrame(state => {
    if (meshRef.current) {
      const time = state.clock.getElapsedTime();
      const scale = 1 + Math.sin(time * 2 + node.id.charCodeAt(0)) * 0.05;
      meshRef.current.scale.setScalar(hovered ? 1.3 : scale);
    }
  });

  const color = statusColors[node.status] ?? '#666';

  return (
    <group>
      <Sphere
        ref={meshRef}
        args={[0.25, 32, 32]}
        position={[node.x, node.y, node.z]}
        onClick={() => onClick?.(node)}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
      >
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={hovered ? 0.8 : 0.4}
          transparent
          opacity={0.9}
        />
      </Sphere>

      {/* Hide the floating label when the popup is open so getByText finds exactly one match */}
      {!isSelected && (
      <Html position={[node.x, node.y + 0.4, node.z]} distanceFactor={10}>
        <div
          style={{
            background: hovered ? 'rgba(33,150,243,0.95)' : 'rgba(0,0,0,0.8)',
            color: 'white',
            padding: '4px 10px',
            borderRadius: 4,
            whiteSpace: 'nowrap',
            fontSize: 11,
            fontFamily: 'monospace',
            border: hovered ? '1px solid #2196f3' : '1px solid #666',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          {node.name}
        </div>
      </Html>
      )}
    </group>
  );
}

function ParticleField() {
  const particlesRef = useRef<THREE.Points>(null);
  const particleCount = 300;

  const positions = useMemo(() => {
    const pos = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = 4 + Math.random() * 3;
      pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      pos[i * 3 + 2] = r * Math.cos(phi);
    }
    return pos;
  }, []);

  useFrame(() => {
    if (particlesRef.current) {
      particlesRef.current.rotation.y += 0.0003;
      particlesRef.current.rotation.x += 0.0001;
    }
  });

  return (
    <points ref={particlesRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={particleCount}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.03}
        color="#00bcd4"
        transparent
        opacity={0.4}
        sizeAttenuation
      />
    </points>
  );
}

function StatusRing() {
  const ringRef = useRef<THREE.Mesh>(null);

  useFrame(() => {
    if (ringRef.current) {
      ringRef.current.rotation.z += 0.002;
    }
  });

  return (
    <mesh ref={ringRef}>
      <torusGeometry args={[4.5, 0.01, 16, 100]} />
      <meshBasicMaterial color="#00bcd4" transparent opacity={0.3} />
    </mesh>
  );
}

// ── Default data ──────────────────────────────────────────────────────────────

// Default nodes reflect the actual registered QwickForge services.
// "Forge" = orchestration engine (formerly openclaw), "Runner" = agent execution service (formerly AISE).
const defaultNodes: SystemNode[] = [
  {
    id: 'forge',
    name: 'Forge',
    type: 'agent',
    status: 'busy',
    x: 2.5,
    y: 0.5,
    z: 0.5,
    description: 'Orchestration engine',
    metrics: [{ label: 'Tasks', value: '3' }, { label: 'Last Deploy', value: '2m ago' }],
    controlUrl: '/forge',
    currentTask: 'Deploy PR#1324',
  },
  {
    id: 'runner',
    name: 'Runner',
    type: 'agent',
    status: 'online',
    x: -2.3,
    y: 0.8,
    z: 0.3,
    description: 'Agent execution service',
    metrics: [{ label: 'Active', value: '2' }, { label: 'Queued', value: '0' }],
  },
  {
    id: 'mcp-aggregator',
    name: 'MCP Aggregator',
    type: 'service',
    status: 'online',
    x: 0.5,
    y: -2.2,
    z: 0.8,
    description: 'MCP tool aggregation service',
    metrics: [{ label: 'Tools', value: '42' }, { label: 'Latency', value: '8ms' }],
  },
  {
    id: 'secrets',
    name: 'Secrets',
    type: 'service',
    status: 'online',
    x: -0.8,
    y: 2.1,
    z: -0.5,
    description: 'Encrypted secrets management',
    metrics: [{ label: 'Envs', value: '12' }],
  },
  {
    id: 'documents',
    name: 'Documents',
    type: 'service',
    status: 'online',
    x: 0,
    y: 0,
    z: 2.5,
    description: 'Knowledge base documents service',
    metrics: [{ label: 'Docs', value: '84' }, { label: 'Pages', value: '312' }],
    lastDeploy: '1h ago',
  },
  {
    id: 'memories',
    name: 'Memories',
    type: 'service',
    status: 'online',
    x: -1.8,
    y: -1.2,
    z: 1.5,
    description: 'Agent memory service',
    metrics: [{ label: 'Entries', value: '200+' }],
  },
  {
    id: 'projects-api',
    name: 'Projects API',
    type: 'service',
    status: 'online',
    x: 2,
    y: -1.5,
    z: -1.5,
    description: 'Goals & Items Service',
    metrics: [{ label: 'Goals', value: '2' }, { label: 'Items', value: '18' }],
    lastDeploy: '3h ago',
  },
  {
    id: 'beszel',
    name: 'Beszel',
    type: 'infrastructure',
    status: 'online',
    x: -2,
    y: -1,
    z: -1.5,
    description: 'Infrastructure Monitoring',
    metrics: [{ label: 'VMs', value: '5' }, { label: 'Alerts', value: '0' }],
    controlUrl: undefined, // Beszel not yet publicly accessible; remove link for now
  },
  {
    id: 'github',
    name: 'GitHub',
    type: 'service',
    status: 'online',
    x: -1.5,
    y: -2,
    z: -2,
    description: 'Repository & PRs',
    metrics: [{ label: 'Open PRs', value: '3' }, { label: 'Commits', value: '15' }],
    lastDeploy: undefined,
  },
];

const defaultConnections: Connection[] = [
  { from: 'forge', to: 'projects-api', status: 'active' },
  { from: 'runner', to: 'forge', status: 'active' },
  { from: 'mcp-aggregator', to: 'documents', status: 'active' },
  { from: 'mcp-aggregator', to: 'memories', status: 'active' },
  { from: 'mcp-aggregator', to: 'secrets', status: 'active' },
  { from: 'mcp-aggregator', to: 'projects-api', status: 'active' },
  { from: 'forge', to: 'mcp-aggregator', status: 'active' },
  { from: 'beszel', to: 'forge', status: 'inactive' },
  { from: 'projects-api', to: 'github', status: 'active' },
];

// ── Main component ────────────────────────────────────────────────────────────

export default function SystemGlobe({
  agents: propAgents,
  nodes: propNodes,
  connections: propConnections,
  loading = false,
  onNodeClick,
}: SystemGlobeProps) {
  const [selectedNode, setSelectedNode] = useState<SystemNode | null>(null);

  // Convert simple agents → full SystemNode objects
  const agentNodes: SystemNode[] | undefined = propAgents?.map(a => ({
    ...a,
    type: a.type ?? 'agent',
  }));

  const nodes =
    agentNodes?.length
      ? agentNodes
      : propNodes?.length
      ? propNodes
      : defaultNodes;

  const connections = propConnections?.length ? propConnections : defaultConnections;

  const handleNodeClick = (node: SystemNode) => {
    setSelectedNode(prev => (prev?.id === node.id ? null : node));
    onNodeClick?.(node);
  };

  const stats = useMemo(
    () => ({
      total: nodes.length,
      online: nodes.filter(n => n.status === 'online').length,
      busy: nodes.filter(n => n.status === 'busy').length,
      offline: nodes.filter(n => n.status === 'offline').length,
      warning: nodes.filter(n => n.status === 'warning' || n.status === 'error').length,
    }),
    [nodes],
  );

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 400,
        }}
      >
        <div role="progressbar" aria-label="loading" style={{
          width: 24,
          height: 24,
          border: '2px solid var(--theme-border-color)',
          borderTopColor: 'var(--theme-color-primary)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        height: 450,
        borderRadius: 8,
        overflow: 'hidden',
        background: 'var(--theme-bg-surface)',
        border: '1px solid var(--theme-border-color)',
      }}
    >
      <Canvas camera={{ position: [0, 0, 10], fov: 50 }}>
        <ambientLight intensity={0.4} />
        <pointLight position={[10, 10, 10]} intensity={1} color="#fff" />
        <pointLight position={[-10, -10, -10]} intensity={0.5} color="#00bcd4" />
        <NetworkGlobe
          nodes={nodes}
          connections={connections}
          onNodeClick={handleNodeClick}
          selectedNodeId={selectedNode?.id}
        />
        <StatusRing />
        <ParticleField />
        <OrbitControls enableZoom={true} enablePan={false} minDistance={6} maxDistance={18} />
      </Canvas>

      {/* System Health legend — top-left */}
      <div
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          padding: 12,
          borderRadius: 8,
          background: 'rgba(0,0,0,0.85)',
          border: '1px solid #333',
        }}
      >
        <Text variant="subtitle2" content="System Health" customColor="#00bcd4" />
        <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
          <StatusItem color="#4caf50" count={stats.online} label="Online" />
          <StatusItem color="#ff9800" count={stats.busy} label="Busy" />
          <StatusItem color="#f44336" count={stats.warning} label="Issues" />
          <StatusItem color="#9e9e9e" count={stats.offline} label="Offline" />
        </div>
        <Text
          variant="caption"
          content={`${nodes.length} nodes • ${connections.filter(c => c.status === 'active').length} active connections`}
          customColor="#888"
        />
      </div>

      {/* Type legend — top-right */}
      <div
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          gap: 8,
        }}
      >
        <LegendItem color="#4caf50" label="Agent" />
        <LegendItem color="#2196f3" label="Service" />
        <LegendItem color="#9c27b0" label="Infra" />
      </div>

      {/* Node detail popup — top-right area, below legend */}
      {selectedNode && (
        <NodePopup
          node={selectedNode}
          onClose={() => setSelectedNode(null)}
        />
      )}
    </div>
  );
}

// ── Node popup ────────────────────────────────────────────────────────────────

function NodePopup({
  node,
  onClose,
}: {
  node: SystemNode;
  onClose: () => void;
}) {
  const statusColor: Record<string, string> = {
    online: '#4caf50',
    offline: '#9e9e9e',
    busy: '#ff9800',
    warning: '#ff9800',
    error: '#f44336',
  };

  return (
    <div
      data-testid="node-popup"
      style={{
        position: 'absolute',
        bottom: 12,
        right: 12,
        width: 260,
        padding: 16,
        borderRadius: 8,
        background: 'rgba(10,10,20,0.95)',
        border: '1px solid #333',
        zIndex: 10,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: statusColor[node.status] ?? '#9e9e9e',
            flexShrink: 0,
          }}
        />
        <Text variant="subtitle2" content={node.name} customColor="white" />
        <div
          onClick={onClose}
          style={{
            marginLeft: 'auto',
            cursor: 'pointer',
            color: '#999',
            fontSize: 16,
            lineHeight: 1,
          }}
        >
          ×
        </div>
      </div>

      <Text
        variant="caption"
        content={`Status: ${node.status}`}
        customColor={statusColor[node.status] ?? '#9e9e9e'}
      />

      {node.description && (
        <Text
          variant="body2"
          content={node.description}
          customColor="#aaa"
        />
      )}

      {/* Metrics */}
      {node.metrics && node.metrics.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {node.metrics.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text variant="caption" content={m.label} customColor="#888" />
              <Text variant="caption" content={m.value} customColor="#ccc" />
            </div>
          ))}
        </div>
      )}

      {/* Agent-specific actions */}
      {node.type === 'agent' && (
        <div style={{ marginTop: 12 }}>
          {node.currentTask && (
            <Text
              variant="caption"
              content={`Task: ${node.currentTask}`}
              customColor="#bbb"
            />
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
            {node.githubIssue && (
              <a
                href={node.githubIssue}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#2196f3', fontSize: 12, textDecoration: 'none' }}
              >
                View Issue
              </a>
            )}
            <span style={{ fontSize: 12, color: '#4caf50', cursor: 'pointer' }}>
              Claim Task
            </span>
            <span style={{ fontSize: 12, color: '#f44336', cursor: 'pointer' }}>
              Release Task
            </span>
          </div>
        </div>
      )}

      {/* Service-specific actions */}
      {node.type === 'service' && (
        <div style={{ marginTop: 12 }}>
          {node.lastDeploy && (
            <Text
              variant="caption"
              content={`Last deploy: ${node.lastDeploy}`}
              customColor="#888"
            />
          )}
          {node.controlUrl && (
            <a
              href={node.controlUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#2196f3', fontSize: 12, textDecoration: 'none', display: 'block' }}
            >
              Go to Deploy Page
            </a>
          )}
        </div>
      )}

      {/* Infrastructure-specific actions */}
      {node.type === 'infrastructure' && (
        <div style={{ marginTop: 12 }}>
          {node.blockerReason && (
            <Text
              variant="caption"
              content={`Blocker: ${node.blockerReason}`}
              customColor="#f44336"
            />
          )}
          {node.controlUrl && (
            <a
              href={node.controlUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#2196f3', fontSize: 12, textDecoration: 'none', display: 'block' }}
            >
              Open Control Panel
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ── Status / legend helpers ───────────────────────────────────────────────────

function StatusItem({
  color,
  count,
  label,
}: {
  color: string;
  count: number;
  label: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <div style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
      <Text variant="caption" content={label} customColor="#ccc" />
      <Text variant="caption" content={String(count)} customColor="#888" />
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        background: 'rgba(0,0,0,0.7)',
        padding: '4px 8px',
        borderRadius: 4,
        fontSize: 10,
      }}
    >
      <div style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
      <span style={{ color: '#ccc' }}>{label}</span>
    </div>
  );
}
